import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, KeyRound, Globe, CalendarClock, Twitter, Save, Loader2, Mail, ShoppingBag, Link2, Unlink, CheckCircle2, XCircle } from "lucide-react";
import SubscriptionManager from "@/components/SubscriptionManager";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function Settings() {
  const { user } = useAuth();
  const [networks, setNetworks] = useState([]);
  const [prefs, setPrefs] = useState(null);

  // Digest form
  const [digestDay, setDigestDay] = useState("Sunday");
  const [digestHour, setDigestHour] = useState(9);
  const [digestEnabled, setDigestEnabled] = useState(true);
  const [savingDigest, setSavingDigest] = useState(false);
  const [digestMsg, setDigestMsg] = useState("");

  // Twitter form
  const [twApiKey, setTwApiKey] = useState("");
  const [twApiSecret, setTwApiSecret] = useState("");
  const [twAccessToken, setTwAccessToken] = useState("");
  const [twAccessSecret, setTwAccessSecret] = useState("");
  const [twAutoPost, setTwAutoPost] = useState(false);
  const [savingTwitter, setSavingTwitter] = useState(false);
  const [twMsg, setTwMsg] = useState("");

  // Email/Resend form
  const [resendKey, setResendKey] = useState("");
  const [resendFrom, setResendFrom] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailMsg, setEmailMsg] = useState("");

  // Amazon tag
  const [amazonTag, setAmazonTag] = useState(user?.amazon_tag || "");
  const [savingTag, setSavingTag] = useState(false);
  const [tagMsg, setTagMsg] = useState("");

  const saveAmazonTag = async () => {
    setSavingTag(true); setTagMsg("");
    try {
      const { data } = await api.put("/preferences/amazon-tag", { amazon_tag: amazonTag });
      setTagMsg(data.amazon_tag ? `Saved. Every Amazon link on your content now uses tag=${data.amazon_tag}` : "Cleared — platform default will be used.");
    } catch (e) {
      setTagMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingTag(false);
      setTimeout(() => setTagMsg(""), 4500);
    }
  };

  const loadPrefs = async () => {
    try {
      const [n, p] = await Promise.all([
        api.get("/affiliate/networks"),
        api.get("/preferences"),
      ]);
      setNetworks(n.data);
      setPrefs(p.data);
      const d = p.data.digest || {};
      setDigestDay(d.day_of_week || "Sunday");
      setDigestHour(d.hour_utc ?? 9);
      setDigestEnabled(d.enabled ?? true);
      setTwAutoPost(p.data.twitter?.auto_post_on_publish || false);
      setResendFrom(p.data.email?.from_email || "");
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadPrefs(); }, []);

  const saveDigest = async () => {
    setSavingDigest(true); setDigestMsg("");
    try {
      const { data } = await api.put("/preferences/digest", {
        day_of_week: digestDay,
        hour_utc: digestHour,
        enabled: digestEnabled,
      });
      setDigestMsg(`Saved — next digest ${data.digest.day_of_week} at ${String(data.digest.hour_utc).padStart(2,"0")}:00 UTC`);
      await loadPrefs();
    } catch (e) {
      setDigestMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingDigest(false);
      setTimeout(() => setDigestMsg(""), 4000);
    }
  };

  const saveTwitter = async () => {
    setSavingTwitter(true); setTwMsg("");
    try {
      await api.put("/preferences/twitter", {
        api_key: twApiKey,
        api_secret: twApiSecret,
        access_token: twAccessToken,
        access_secret: twAccessSecret,
        auto_post_on_publish: twAutoPost,
      });
      setTwMsg(twApiKey ? "Twitter keys saved (encrypted at rest)." : "Cleared.");
      setTwApiKey(""); setTwApiSecret(""); setTwAccessToken(""); setTwAccessSecret("");
      await loadPrefs();
    } catch (e) {
      setTwMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingTwitter(false);
      setTimeout(() => setTwMsg(""), 4000);
    }
  };

  const twitterConfigured = prefs?.twitter?.configured;
  const twitterMasked = prefs?.twitter?.masked || {};
  const emailConfigured = prefs?.email?.configured;
  const emailMaskedKey = prefs?.email?.masked_key || "";

  const saveEmail = async () => {
    setSavingEmail(true); setEmailMsg("");
    try {
      await api.put("/preferences/email", {
        resend_api_key: resendKey,
        from_email: resendFrom,
      });
      setEmailMsg(resendKey ? "Resend key saved — digests will send to your inbox from now on." : "Cleared.");
      setResendKey("");
      await loadPrefs();
    } catch (e) {
      setEmailMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingEmail(false);
      setTimeout(() => setEmailMsg(""), 4000);
    }
  };

  return (
    <div className="space-y-8 fade-up">
      <div className="border-b border-slate-800 pb-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-2">— Settings</p>
        <h1 className="font-serif text-5xl leading-none text-sky-100 tracking-tight">Configuration</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-slate-800 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <KeyRound className="w-5 h-5 text-sky-400" />
            <h3 className="font-serif text-xl text-sky-100">Account</h3>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-slate-800 py-2">
              <span className="text-neutral-500">Email</span>
              <span className="text-sky-100 font-mono">{user?.email}</span>
            </div>
            <div className="flex justify-between border-b border-slate-800 py-2">
              <span className="text-neutral-500">Name</span>
              <span className="text-sky-100">{user?.name}</span>
            </div>
            <div className="flex justify-between border-b border-slate-800 py-2">
              <span className="text-neutral-500">Role</span>
              <span className={`font-mono text-xs px-2 py-0.5 rounded border ${user?.role === "admin" ? "border-sky-400 text-sky-300" : "border-slate-700 text-neutral-400"}`}>
                {user?.role}
              </span>
            </div>
            <div className="flex justify-between border-b border-slate-800 py-2">
              <span className="text-neutral-500">Signed up</span>
              <span className="text-sky-100 font-mono text-xs">{user?.created_at?.slice(0, 10)}</span>
            </div>
          </div>
        </div>

        <div className="border border-slate-800 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="w-5 h-5 text-sky-400" />
            <h3 className="font-serif text-xl text-sky-100">Security</h3>
          </div>
          <ul className="space-y-2 text-sm text-neutral-400">
            <li className="flex justify-between border-b border-slate-800 py-2"><span>Password hashing</span><span className="text-emerald-400 font-mono text-xs">bcrypt (12 rounds)</span></li>
            <li className="flex justify-between border-b border-slate-800 py-2"><span>Session token</span><span className="text-emerald-400 font-mono text-xs">JWT · HS256 · 24h</span></li>
            <li className="flex justify-between border-b border-slate-800 py-2"><span>Brute-force lockout</span><span className="text-emerald-400 font-mono text-xs">5 attempts / 15min</span></li>
            <li className="flex justify-between border-b border-slate-800 py-2"><span>Cookies</span><span className="text-emerald-400 font-mono text-xs">httpOnly · SameSite=Lax</span></li>
            <li className="flex justify-between border-b border-slate-800 py-2"><span>API credentials at rest</span><span className="text-emerald-400 font-mono text-xs">Fernet AES-128-CBC</span></li>
          </ul>
        </div>
      </div>

      {/* Amazon Associates Tracking ID */}
      <div data-testid="amazon-tag-panel" className="border border-slate-800 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <ShoppingBag className="w-5 h-5 text-sky-400" />
          <div className="flex-1">
            <h3 className="font-serif text-xl text-sky-100">Amazon Associates Tracking ID</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Your Amazon partner tag. Every Amazon affiliate link on your content will use YOUR tag so referrals credit you. Leave blank to use the platform default.
            </p>
          </div>
          {user?.amazon_tag && (
            <span className="text-[10px] font-mono px-2 py-1 rounded-full border border-emerald-500/40 text-emerald-400 bg-emerald-500/5">● active</span>
          )}
        </div>
        <div className="flex flex-col md:flex-row gap-3">
          <input
            data-testid="input-amazon-tag"
            value={amazonTag}
            onChange={(e) => setAmazonTag(e.target.value)}
            placeholder="e.g. yourbrand-20"
            className="flex-1 bg-slate-900 border border-slate-800 rounded px-3 py-2.5 text-sky-50 font-mono text-sm focus:border-sky-400 outline-none"
          />
          <button
            data-testid="btn-save-amazon-tag"
            onClick={saveAmazonTag}
            disabled={savingTag}
            className="py-2.5 px-5 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded font-medium flex items-center gap-2 disabled:opacity-60 whitespace-nowrap"
          >
            {savingTag ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save tag
          </button>
        </div>
        {tagMsg && <p data-testid="amazon-tag-msg" className="mt-3 text-xs text-sky-300 font-mono">{tagMsg}</p>}
      </div>

      {/* Digest schedule */}
      <div data-testid="digest-panel" className="border border-slate-800 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <CalendarClock className="w-5 h-5 text-sky-400" />
          <div>
            <h3 className="font-serif text-xl text-sky-100">Weekly digest</h3>
            <p className="text-xs text-neutral-500 mt-0.5">Pick the day and hour (UTC) your refresh summary arrives.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">Day</label>
            <select
              data-testid="digest-day"
              value={digestDay}
              onChange={(e) => setDigestDay(e.target.value)}
              className="w-full bg-slate-900 border border-slate-800 rounded py-2.5 px-3 text-sky-50 focus:border-sky-400 outline-none"
            >
              {DAYS.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">Hour (UTC)</label>
            <select
              data-testid="digest-hour"
              value={digestHour}
              onChange={(e) => setDigestHour(Number(e.target.value))}
              className="w-full bg-slate-900 border border-slate-800 rounded py-2.5 px-3 text-sky-50 focus:border-sky-400 outline-none font-mono"
            >
              {HOURS.map((h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-3 cursor-pointer border border-slate-800 rounded p-3 hover:border-sky-400/40 transition w-full">
              <input
                type="checkbox"
                data-testid="digest-enabled"
                checked={digestEnabled}
                onChange={(e) => setDigestEnabled(e.target.checked)}
                className="accent-sky-400 w-4 h-4"
              />
              <span className="text-sm text-sky-100">Enabled</span>
            </label>
          </div>
          <div className="flex items-end">
            <button
              data-testid="btn-save-digest"
              onClick={saveDigest}
              disabled={savingDigest}
              className="w-full py-2.5 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded font-medium flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {savingDigest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
          </div>
        </div>
        {digestMsg && <p data-testid="digest-msg" className="mt-3 text-xs text-sky-300 font-mono">{digestMsg}</p>}
      </div>

      {/* Resend email delivery */}
      <div data-testid="resend-panel" className="border border-slate-800 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Mail className="w-5 h-5 text-sky-400" />
          <div className="flex-1">
            <h3 className="font-serif text-xl text-sky-100">Live email delivery (Resend)</h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              Paste a Resend API key to send weekly digests to your actual inbox. Without it, digests are queued log-only.
              Grab a free key at{" "}
              <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-sky-300 underline">resend.com/api-keys</a>.
            </p>
          </div>
          {emailConfigured && (
            <span data-testid="email-status" className="text-[10px] font-mono px-2 py-1 rounded-full border border-emerald-500/40 text-emerald-400 bg-emerald-500/5">● live</span>
          )}
        </div>

        {emailConfigured && (
          <div className="border border-emerald-500/20 bg-emerald-500/5 rounded p-3 mb-4 text-xs font-mono">
            <p className="text-neutral-400">Resend key: <span className="text-emerald-300">{emailMaskedKey || "—"}</span></p>
            {prefs?.email?.from_email && <p className="text-neutral-400">From: <span className="text-emerald-300">{prefs.email.from_email}</span></p>}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">Resend API Key</label>
            <input
              data-testid="resend-key"
              type="password"
              value={resendKey}
              onChange={(e) => setResendKey(e.target.value)}
              placeholder={emailConfigured ? "•••• leave blank to keep current" : "re_XXXXXXXXXXXXXXXX"}
              className="w-full bg-slate-900 border border-slate-800 rounded px-3 py-2.5 text-sky-50 font-mono text-sm focus:border-sky-400 outline-none"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">From address <span className="text-slate-600">(optional)</span></label>
            <input
              data-testid="resend-from"
              value={resendFrom}
              onChange={(e) => setResendFrom(e.target.value)}
              placeholder="Your Name <you@yourdomain.com>"
              className="w-full bg-slate-900 border border-slate-800 rounded px-3 py-2.5 text-sky-50 font-mono text-sm focus:border-sky-400 outline-none"
            />
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <button
            data-testid="btn-save-email"
            onClick={saveEmail}
            disabled={savingEmail}
            className="py-2.5 px-5 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded font-medium flex items-center gap-2 disabled:opacity-60"
          >
            {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Resend key
          </button>
          {emailMsg && <p data-testid="email-msg" className="text-xs text-sky-300 font-mono">{emailMsg}</p>}
        </div>
      </div>

      {/* Subscription manager — active plan + cancel + payment history */}
      <SubscriptionManager />

      {/* Twitter/X OAuth 2.0 — one-click connect */}
      <TwitterOAuth2Panel />

      {/* Twitter/X credentials */}
      <div data-testid="twitter-panel" className="border border-slate-800 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Twitter className="w-5 h-5 text-sky-400" />
          <div className="flex-1">
            <h3 className="font-serif text-xl text-sky-100">X (Twitter) auto-post</h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              Paste your X API keys to auto-post every new article. Keys are encrypted at rest with Fernet.
              Get yours at{" "}
              <a href="https://developer.twitter.com/en/portal/dashboard" target="_blank" rel="noreferrer" className="text-sky-300 underline">developer.twitter.com</a>.
            </p>
          </div>
          {twitterConfigured && (
            <span data-testid="twitter-status" className="text-[10px] font-mono px-2 py-1 rounded-full border border-emerald-500/40 text-emerald-400 bg-emerald-500/5">● live</span>
          )}
        </div>

        {twitterConfigured && (
          <div className="border border-emerald-500/20 bg-emerald-500/5 rounded p-3 mb-4 text-xs font-mono grid grid-cols-1 md:grid-cols-2 gap-1">
            <p className="text-neutral-400">API key: <span className="text-emerald-300">{twitterMasked.api_key || "—"}</span></p>
            <p className="text-neutral-400">API secret: <span className="text-emerald-300">{twitterMasked.api_secret || "—"}</span></p>
            <p className="text-neutral-400">Access token: <span className="text-emerald-300">{twitterMasked.access_token || "—"}</span></p>
            <p className="text-neutral-400">Access secret: <span className="text-emerald-300">{twitterMasked.access_secret || "—"}</span></p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">API Key (consumer key)</label>
            <input
              data-testid="tw-api-key"
              type="password"
              value={twApiKey}
              onChange={(e) => setTwApiKey(e.target.value)}
              placeholder={twitterConfigured ? "•••• leave blank to keep current" : "e.g. Abc123XyZ…"}
              className="w-full bg-slate-900 border border-slate-800 rounded px-3 py-2.5 text-sky-50 font-mono text-sm focus:border-sky-400 outline-none"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">API Secret</label>
            <input
              data-testid="tw-api-secret"
              type="password"
              value={twApiSecret}
              onChange={(e) => setTwApiSecret(e.target.value)}
              placeholder={twitterConfigured ? "•••• leave blank to keep current" : ""}
              className="w-full bg-slate-900 border border-slate-800 rounded px-3 py-2.5 text-sky-50 font-mono text-sm focus:border-sky-400 outline-none"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">Access Token</label>
            <input
              data-testid="tw-access-token"
              type="password"
              value={twAccessToken}
              onChange={(e) => setTwAccessToken(e.target.value)}
              placeholder={twitterConfigured ? "•••• leave blank to keep current" : ""}
              className="w-full bg-slate-900 border border-slate-800 rounded px-3 py-2.5 text-sky-50 font-mono text-sm focus:border-sky-400 outline-none"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">Access Secret</label>
            <input
              data-testid="tw-access-secret"
              type="password"
              value={twAccessSecret}
              onChange={(e) => setTwAccessSecret(e.target.value)}
              placeholder={twitterConfigured ? "•••• leave blank to keep current" : ""}
              className="w-full bg-slate-900 border border-slate-800 rounded px-3 py-2.5 text-sky-50 font-mono text-sm focus:border-sky-400 outline-none"
            />
          </div>
        </div>

        <label className="mt-4 flex items-center gap-3 cursor-pointer border border-slate-800 rounded p-3 hover:border-sky-400/40 transition">
          <input
            type="checkbox"
            data-testid="tw-auto-post"
            checked={twAutoPost}
            onChange={(e) => setTwAutoPost(e.target.checked)}
            className="accent-sky-400 w-4 h-4"
          />
          <div className="flex-1">
            <p className="text-sm text-sky-100">Auto-tweet every published article</p>
            <p className="text-[11px] text-neutral-500">Composes with GPT-5.6 Terra, posts immediately after each article is published.</p>
          </div>
        </label>

        <div className="mt-5 flex items-center gap-3">
          <button
            data-testid="btn-save-twitter"
            onClick={saveTwitter}
            disabled={savingTwitter}
            className="py-2.5 px-5 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded font-medium flex items-center gap-2 disabled:opacity-60"
          >
            {savingTwitter ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save X credentials
          </button>
          {twMsg && <p data-testid="twitter-msg" className="text-xs text-sky-300 font-mono">{twMsg}</p>}
        </div>
      </div>

      <div className="border border-slate-800 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-5">
          <Globe className="w-5 h-5 text-sky-400" />
          <h3 className="font-serif text-xl text-sky-100">Supported affiliate networks</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {networks.map((n) => (
            <div key={n.key} className="border border-slate-800 rounded p-3 hover:border-sky-400/40 transition">
              <p className="font-serif text-sky-100">{n.name}</p>
              <p className="text-[10px] font-mono text-neutral-500 mt-1">{n.commission_range}</p>
              <p className="text-[10px] font-mono text-sky-300/70">{n.cookie_days}d cookie</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* --------- Twitter/X OAuth 2.0 connect panel --------- */
function TwitterOAuth2Panel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/auth/twitter/status");
      setStatus(data);
    } catch {
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const callbackUrl = `${window.location.origin}/auth/twitter/callback`;
      const { data } = await api.get("/auth/twitter/start", { params: { callback_url: callbackUrl } });
      window.location.assign(data.authorize_url);
    } catch (e) {
      setMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Could not start connection.");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect your X account? Auto-posts will stop.")) return;
    setBusy(true);
    try {
      await api.post("/auth/twitter/disconnect");
      setStatus({ connected: false });
      setMsg("X account disconnected.");
    } catch (e) {
      setMsg(formatApiErrorDetail(e.response?.data?.detail) || "Failed to disconnect");
    } finally {
      setBusy(false);
    }
  };

  const toggleAutoPost = async (enabled) => {
    setBusy(true);
    try {
      await api.put("/auth/twitter/auto-post", { enabled });
      setStatus((prev) => ({ ...prev, auto_post_on_publish: enabled }));
    } catch (e) {
      setMsg(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  const toggleInstantPost = async (enabled) => {
    setBusy(true);
    try {
      await api.put("/auth/twitter/instant-post", { enabled });
      setStatus((prev) => ({ ...prev, instant_post: enabled }));
    } catch (e) {
      setMsg(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="twitter-oauth2-panel" className="border border-slate-800 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <Twitter className="w-5 h-5 text-sky-400" />
        <div className="flex-1">
          <h3 className="font-serif text-xl text-sky-100">Connect X (one-click OAuth 2.0)</h3>
          <p className="text-xs text-neutral-500 mt-0.5">
            Sign in with your X account to auto-post every published article. No copy-pasting API keys.
            Uses OAuth 2.0 with PKCE + <span className="font-mono">tweet.write</span> scope.
          </p>
        </div>
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
        ) : status?.connected ? (
          <span data-testid="twitter-oauth2-status-live" className="text-[10px] font-mono px-2 py-1 rounded-full border border-emerald-500/40 text-emerald-400 bg-emerald-500/5">● connected</span>
        ) : (
          <span data-testid="twitter-oauth2-status-off" className="text-[10px] font-mono px-2 py-1 rounded-full border border-slate-700 text-slate-500">○ not connected</span>
        )}
      </div>

      {status?.connected ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 border border-emerald-500/20 bg-emerald-500/5 rounded p-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <div className="flex-1">
              <p className="text-sm text-sky-100">
                {status.name || status.username || "Connected"}{" "}
                {status.username && <span className="text-sky-300 font-mono">@{status.username}</span>}
              </p>
              <p className="text-[11px] text-slate-500 font-mono">
                scopes: {(status.scopes || []).join(" · ")}
              </p>
            </div>
          </div>
          <label className="flex items-center gap-3 border border-slate-800 rounded p-3 hover:border-sky-400/40 transition cursor-pointer">
            <input
              type="checkbox"
              data-testid="twitter-oauth2-auto-post"
              checked={!!status.auto_post_on_publish}
              onChange={(e) => toggleAutoPost(e.target.checked)}
              disabled={busy}
              className="accent-sky-400 w-4 h-4"
            />
            <div className="flex-1">
              <p className="text-sm text-sky-100">Auto-post on article publish</p>
              <p className="text-[11px] text-slate-500">A short tweet with the article title + trackable link is posted.</p>
            </div>
          </label>
          <label
            className={`flex items-center gap-3 border rounded p-3 transition cursor-pointer ${status.auto_post_on_publish ? "border-slate-800 hover:border-sky-400/40" : "border-slate-900 opacity-50 cursor-not-allowed"}`}
          >
            <input
              type="checkbox"
              data-testid="twitter-oauth2-instant-post"
              checked={!!status.instant_post}
              onChange={(e) => toggleInstantPost(e.target.checked)}
              disabled={busy || !status.auto_post_on_publish}
              className="accent-sky-400 w-4 h-4"
            />
            <div className="flex-1">
              <p className="text-sm text-sky-100">Skip preview — post to X instantly</p>
              <p className="text-[11px] text-slate-500">
                When ON, articles go live on X the moment they publish (no approval modal).
                When OFF, tweets queue as a draft for you to review first.
              </p>
            </div>
          </label>
          <button
            data-testid="btn-twitter-disconnect"
            onClick={disconnect}
            disabled={busy}
            className="px-4 py-2 border border-red-500/40 text-red-300 hover:bg-red-500/10 rounded font-medium text-sm inline-flex items-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
            Disconnect
          </button>
        </div>
      ) : (
        <button
          data-testid="btn-twitter-connect"
          onClick={connect}
          disabled={busy || loading}
          className="px-5 py-2.5 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded font-medium inline-flex items-center gap-2 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
          Connect X account
        </button>
      )}
      {msg && (
        <p data-testid="twitter-oauth2-msg" className="mt-3 text-xs text-slate-400">{msg}</p>
      )}
    </div>
  );
}
